import { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { RefreshCw, Info, Copy, X } from 'lucide-react'
import { toast } from 'sonner'
import { useEosTransaction } from '@/hooks/useEosTransaction'
import { useClipboard } from '@/hooks/useClipboard'
import { CommonFooter } from '@/components/layout'
import { PrivateKey } from '@wharfkit/antelope'
import { getFreeQuota, createAccount as createAccountApi } from '@/services/api/account'
import { toFixed } from '@/lib/decimal'

import poolHeader from '@/assets/images/pool-header.png'

export function CreateAccount() {
  const { t } = useTranslation()
  const { transact, loading: txLoading } = useEosTransaction()
  const { copy } = useClipboard()

  const [accountName, setAccountName] = useState('')
  const [publicKey, setPublicKey] = useState('')
  const [isFree, setIsFree] = useState(false)
  const [captchaToken, setCaptchaToken] = useState('')
  const [freeLeft, setFreeLeft] = useState(0)
  const [loading, setLoading] = useState(false)
  const [transferAmount] = useState('0.06')
  const [generatedPublicKey, setGeneratedPublicKey] = useState('')
  const [generatedPrivateKey, setGeneratedPrivateKey] = useState('')
  const [showGenerateModal, setShowGenerateModal] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  const freeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Poll free account availability
  const fetchFreeCount = useCallback(async () => {
    try {
      const res = await getFreeQuota()
      if (res.status && res.data) {
        setFreeLeft(res.data.left ?? 0)
      }
    } catch { /* silently fail */ }
  }, [])

  useEffect(() => {
    fetchFreeCount()
    const timer = setInterval(fetchFreeCount, 5000)
    freeTimerRef.current = timer
    return () => { if (freeTimerRef.current) clearInterval(freeTimerRef.current) }
  }, [fetchFreeCount])

  // Random name generator (12 chars, a-z 1-5)
  const randomName = () => {
    const chars = '12345abcdefghijklmnopqrstuvwxyz'
    let name = ''
    for (let i = 0; i < 12; i++) {
      name += chars[Math.floor(Math.random() * chars.length)]
    }
    setAccountName(name)
  }

  // Generate EOS K1 key pair (PUB_K1_ / PVT_K1_ format)
  const handleGenerateKey = async () => {
    try {
      const privateKey = PrivateKey.generate('K1')
      setGeneratedPrivateKey(privateKey.toString())
      setGeneratedPublicKey(privateKey.toPublic().toString())
      setShowGenerateModal(true)
    } catch {
      toast.error('Failed to generate key')
    }
  }

  const handleUseGeneratedKey = () => {
    setShowGenerateModal(false)
    setShowConfirmModal(true)
  }

  const handleConfirmKey = () => {
    setPublicKey(generatedPublicKey)
    setShowConfirmModal(false)
  }

  const validateForm = (): boolean => {
    if (!/^[1-5a-z]{12}$/.test(accountName)) {
      toast.error(t('tools.accountNameTip'))
      return false
    }
    if (!publicKey.trim()) {
      toast.error('Please enter a public key')
      return false
    }
    if (isFree && !captchaToken) {
      toast.error('Please complete the captcha')
      return false
    }
    return true
  }

  const handleSubmit = async () => {
    if (!validateForm()) return
    setLoading(true)

    try {
      if (isFree) {
        // Free account creation via API
        const res = await createAccountApi({
          account: accountName,
          publicKey: publicKey,
          token: captchaToken,
        })
        if (res.status) {
          toast.success(t('swap.success'))
        } else {
          toast.error(res.message || 'Failed')
        }
      } else {
        // Paid: directly invoke wallet transfer
        const quantity = `${toFixed(parseFloat(transferAmount), 4)} EOS`
        const memo = `${accountName}-${publicKey}`

        await transact([{
          account: 'eosio.token',
          name: 'transfer',
          authorization: [{ actor: '', permission: 'active' }],
          data: { from: '', to: 'signup.free', quantity, memo },
        }], t('swap.success'))
      }
    } catch (e: any) {
      toast.error(e?.message || 'Failed')
    } finally {
      setLoading(false)
    }
  }

  const isSubmitting = loading || txLoading

  return (
    <div className="w-full">
      {/* Top warning banner - full width, no border */}
      <div className="w-full bg-[#fef8ec] text-[#ff8e00] text-center text-[12px] md:text-[15px] leading-[1.5] py-[12px] px-[16px]">
        {t('tools.createTip1')}
        <br />
        {t('tools.createTip2')}
      </div>

      <div className="animate-trade-enter">
      <div className="w-full flex justify-center px-4 md:px-[32px] mt-[30px] md:mt-[50px]">
        <div className="w-full max-w-[400px]">
        {/* Totoro header image (sits behind card) */}
        <div className="relative z-0 flex justify-center">
          <img src={poolHeader} className="w-[129px] block" alt="" />
        </div>

        {/* Form card (overlaps image bottom only) */}
        <div className="relative z-10 -mt-[1px] bg-white rounded-[26px] shadow-[0_0_8px_1px_rgba(61,98,50,0.14)] px-[16px] py-[24px]">
          <h3 className="text-[16px] font-bold text-foreground mb-[20px]">{t('tools.createAccount')}</h3>

          {/* Account name */}
          <div className="mb-[16px]">
            <div className="flex items-center justify-between mb-[8px]">
              <label className="text-[13px] text-[#666]">{t('tools.accountName')}</label>
              <button
                onClick={randomName}
                className="text-[12px] text-primary cursor-pointer hover:underline"
              >
                Random
              </button>
            </div>
            <div className="h-[48px] rounded-[16px] border-2 border-[#d1e2ce]">
              <input
                type="text"
                value={accountName}
                onChange={(e) => setAccountName(e.target.value.toLowerCase())}
                placeholder="youraccounts"
                maxLength={12}
                className="w-full h-full px-[16px] bg-transparent text-[14px] text-[#333] outline-none placeholder:text-[#999]"
              />
            </div>
            <p className="text-[12px] text-[#999] mt-[6px]">{t('tools.accountNameTip')}</p>
          </div>

          {/* Public key */}
          <div className="mb-[16px]">
            <div className="flex items-center justify-between mb-[8px]">
              <label className="text-[13px] text-[#666]">Public Key</label>
              <button
                onClick={handleGenerateKey}
                className="text-[12px] text-primary cursor-pointer hover:underline"
              >
                Generate Key
              </button>
            </div>
            <div className="h-[48px] rounded-[16px] border-2 border-[#d1e2ce]">
              <input
                type="text"
                value={publicKey}
                onChange={(e) => setPublicKey(e.target.value)}
                placeholder="EOS..."
                className="w-full h-full px-[16px] bg-transparent text-[14px] text-[#333] outline-none placeholder:text-[#999]"
              />
            </div>
          </div>

          {/* Transfer amount (paid mode) */}
          {!isFree && (
            <div className="mb-[20px]">
              <label className="text-[13px] text-[#666] mb-[8px] block">{t('public.amount')}</label>
              <div className="h-[48px] rounded-[16px] border-2 border-[#d1e2ce] flex items-center px-[16px]">
                <span className="text-[14px] text-[#333]">{transferAmount} EOS</span>
              </div>
            </div>
          )}

          {/* Free account toggle */}
          <div className="flex items-center justify-between mb-[16px]">
            <label className="flex items-center gap-[8px] cursor-pointer">
              <input
                type="checkbox"
                checked={isFree}
                onChange={(e) => { setIsFree(e.target.checked); setCaptchaToken('') }}
                className="w-[16px] h-[16px] accent-[#498846]"
              />
              <span className="text-[13px] text-[#333]">Free account</span>
            </label>
            <span className="text-[12px] text-[#999]">
              {t('tools.remaining')}: {freeLeft}
            </span>
          </div>

          {/* Turnstile CAPTCHA (when free) */}
          {isFree && (
            <TurnstileWidget onVerify={setCaptchaToken} />
          )}

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="w-full h-[48px] rounded-[24px] bg-[#498846] text-white text-[18px] font-semibold cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed hover:opacity-95 transition-opacity flex items-center justify-center mt-[20px]"
          >
            {isSubmitting ? (
              <RefreshCw className="h-5 w-5 animate-spin" />
            ) : (
              t('tools.create')
            )}
          </button>
        </div>

        </div>
      </div>

      {/* Bottom tips - full width, no border/shadow */}
      <div className="w-full mt-[50px] md:mt-[76px] pb-[24px]">
        <div className="max-w-[780px] mx-auto px-4 md:px-[32px]">
          <div className="flex items-center gap-[8px] mb-[12px] text-[#498846]">
            <Info className="w-[18px] h-[18px]" />
            <h4 className="text-[16px] md:text-[18px] font-semibold">{t('tools.createTipTitle')}</h4>
          </div>
          <div className="text-[14px] md:text-[14px] leading-[1.7] text-[#666] space-y-[10px] [&_a]:text-[#ff8e00] [&_a]:underline-offset-2 hover:[&_a]:underline [&_a]:break-all break-words">
            <p>{t('tools.createTip3')}</p>
            <p className="break-all font-mono text-[13px] md:text-[13px] text-[#888]">{t('tools.createTip4')}</p>
            <p dangerouslySetInnerHTML={{ __html: t('tools.createTip5') }} />
            <p dangerouslySetInnerHTML={{ __html: t('tools.createTip6') }} />
          </div>
        </div>
      </div>
      </div>

      <CommonFooter />

      {/* Generate Key Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4" onClick={() => setShowGenerateModal(false)}>
          <div className="relative bg-white rounded-[20px] p-[24px] w-full max-w-[380px]" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setShowGenerateModal(false)}
              className="absolute top-[12px] right-[12px] w-[28px] h-[28px] rounded-full flex items-center justify-center text-[#999] hover:text-[#333] hover:bg-[#f5f5f5] cursor-pointer"
              aria-label="Close"
            >
              <X className="w-[16px] h-[16px]" />
            </button>
            <h4 className="text-[16px] font-bold text-foreground mb-[16px]">Generated Key Pair</h4>

            <div className="mb-[12px]">
              <label className="text-[12px] text-[#666] mb-[4px] block">Public Key</label>
              <div className="bg-[#f5f5f5] rounded-[10px] p-[10px] text-[12px] text-[#333] break-all select-all">
                <span>{generatedPublicKey}</span>
                <button
                  type="button"
                  onClick={() => copy(generatedPublicKey)}
                  className="inline-flex align-middle ml-[6px] text-[#666] hover:text-[#498846] cursor-pointer"
                  aria-label="Copy public key"
                >
                  <Copy className="w-[12px] h-[12px]" />
                </button>
              </div>
            </div>

            <div className="mb-[16px]">
              <label className="text-[12px] text-[#666] mb-[4px] block">Private Key</label>
              <div className="bg-[#fff5f5] rounded-[10px] p-[10px] text-[12px] text-[#c0392b] break-all select-all">
                <span>{generatedPrivateKey}</span>
                <button
                  type="button"
                  onClick={() => copy(generatedPrivateKey)}
                  className="inline-flex align-middle ml-[6px] text-[#c0392b] hover:opacity-80 cursor-pointer"
                  aria-label="Copy private key"
                >
                  <Copy className="w-[12px] h-[12px]" />
                </button>
              </div>
              <p className="text-[11px] text-[#c0392b] mt-[6px]">Please save your private key securely. It cannot be recovered.</p>
            </div>

            <div className="flex gap-[10px]">
              <button
                onClick={handleGenerateKey}
                className="flex-1 h-[40px] rounded-[20px] border border-[#498846] text-[#498846] text-[14px] font-semibold cursor-pointer"
              >
                Regenerate
              </button>
              <button
                onClick={handleUseGeneratedKey}
                className="flex-1 h-[40px] rounded-[20px] bg-[#498846] text-white text-[14px] font-semibold cursor-pointer"
              >
                Use this key
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4" onClick={() => setShowConfirmModal(false)}>
          <div className="bg-white rounded-[20px] p-[24px] w-full max-w-[380px]" onClick={(e) => e.stopPropagation()}>
            <h4 className="text-[16px] font-bold text-foreground mb-[12px]">Confirm</h4>
            <p className="text-[14px] text-[#333] mb-[20px]">Have you saved your private key? This is the only time it will be shown.</p>
            <div className="flex gap-[10px]">
              <button
                onClick={() => { setShowConfirmModal(false); setShowGenerateModal(true) }}
                className="flex-1 h-[40px] rounded-[20px] border border-[#e5e5e5] text-[#666] text-[14px] font-semibold cursor-pointer"
              >
                Go back
              </button>
              <button
                onClick={handleConfirmKey}
                className="flex-1 h-[40px] rounded-[20px] bg-[#498846] text-white text-[14px] font-semibold cursor-pointer"
              >
                Yes, I saved it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function TurnstileWidget({ onVerify }: { onVerify: (token: string) => void }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)

  useEffect(() => {
    const render = () => {
      if (!containerRef.current || widgetIdRef.current !== null) return
      widgetIdRef.current = (window as any).turnstile?.render(containerRef.current, {
        sitekey: '0x4AAAAAAC8xTw4BT9REEsoQ',
        callback: onVerify,
      })
    }

    if ((window as any).turnstile) {
      render()
    } else {
      const existing = document.querySelector('script[src*="turnstile"]')
      if (!existing) {
        const script = document.createElement('script')
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad'
        script.async = true
        ;(window as any).onTurnstileLoad = render
        document.head.appendChild(script)
      } else {
        ;(window as any).onTurnstileLoad = render
      }
    }

    return () => {
      if (widgetIdRef.current !== null) {
        (window as any).turnstile?.remove(widgetIdRef.current)
        widgetIdRef.current = null
      }
    }
  }, [])

  return <div ref={containerRef} className="mt-[12px]" />
}
